import { useEffect, useState, type ReactNode } from "react";
import { getIn, useFormikContext } from "formik";

// @ts-expect-error pure js
import { ReactSelect } from "../UI/Select";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
// @ts-expect-error pure js
import { useBalanceTypes } from "../../hooks/use-balances";
import {
  getBalanceTypeFiscalHint,
  type BalanceTypeOption,
  type BalanceTypeOptionGroup,
} from "../../utils/balances";
import Text from "../UI/Text";
import type { BalanceTypeCatalog } from "../../models/balance";
import styles from "./BalanceTypeCascade.module.css";

type SelectOptions = BalanceTypeOption[] | BalanceTypeOptionGroup[];

interface BalanceTypeCascadeProps {
  prefix?: string;
  /** Catégories d'opération proposées, à plat ou groupées par famille de rattachement. */
  familyOptions: SelectOptions;
  /** Types proposés dans une catégorie. */
  typeOptionsForFamily: (family: string) => BalanceTypeOption[];
  /** Catégorie posée d'office sur une nouvelle opération, si elle est proposée. */
  defaultFamily?: string;
  /** Régimes fiscaux du périmètre, pour l'indication fiscale sous le type. */
  taxRegimes?: Set<string> | null;
  /** Le périmètre déclare une 2072-S (SCI à l'IR) : l'indication cite ses lignes. */
  form2072?: boolean;
  /** Filtre appliqué aux types proposés, affiché à côté du libellé du select de type. */
  typeFilter?: ReactNode;
  disabled?: boolean;
}

function flattenOptions(options: SelectOptions): BalanceTypeOption[] {
  return options.flatMap((option) =>
    "options" in option ? option.options : [option],
  );
}

/**
 * Le choix du type en deux temps : la catégorie d'opération d'abord, puis le type parmi
 * ceux qu'elle regroupe. Sans catégorie, le select de type cherche dans tout le catalogue,
 * groupé par catégorie : qui connaît le nom du type le tape directement. Seul le type est
 * un champ du formulaire ; la catégorie se déduit du type quand il y en a un (édition,
 * modèle de découpe appliqué) et ne vit qu'ici sinon.
 */
export default function BalanceTypeCascade({
  prefix = "",
  familyOptions,
  typeOptionsForFamily,
  defaultFamily,
  taxRegimes = null,
  form2072 = false,
  typeFilter,
  disabled = false,
}: BalanceTypeCascadeProps) {
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const { balanceTypes } = useBalanceTypes() as {
    balanceTypes?: BalanceTypeCatalog;
  };
  const [pickedFamily, setPickedFamily] = useState("");

  const typeName = `${prefix}type`;
  const selectedType = getIn(values, typeName) as string;
  const selectedEntry = selectedType ? balanceTypes?.[selectedType] : undefined;
  const family = selectedEntry?.family ?? pickedFamily;

  const flatFamilies = flattenOptions(familyOptions);
  const familyOption =
    flatFamilies.find((option) => option.value === family) ?? null;

  // Une nouvelle opération part de la catégorie par défaut du contexte, ou de la seule
  // proposée : un clic de moins sur l'opération la plus courante.
  useEffect(() => {
    if (selectedType || pickedFamily || flatFamilies.length === 0) return;
    const preset =
      flatFamilies.length === 1
        ? flatFamilies[0].value
        : flatFamilies.find((option) => option.value === defaultFamily)?.value;
    if (preset) setPickedFamily(preset);
    // Ne rejoue qu'à l'arrivée des catégories : relancer sur chaque rendu réimposerait la
    // catégorie que l'utilisateur vient d'effacer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatFamilies.map((option) => option.value).join(","), defaultFamily]);

  const typeOptions: SelectOptions = family
    ? typeOptionsForFamily(family)
    : flatFamilies
        .map((option) => ({
          label: option.label,
          options: typeOptionsForFamily(option.value),
        }))
        .filter((group) => group.options.length > 0);
  // Un type déjà posé reste visible même si le périmètre courant ne le propose plus
  // (écriture historique, régime fiscal changé) : on ne vide jamais le choix à l'insu de
  // l'utilisateur.
  if (
    selectedEntry &&
    !flattenOptions(typeOptions).some((option) => option.value === selectedType)
  ) {
    (typeOptions as BalanceTypeOption[]).push({
      label: selectedEntry.text,
      value: selectedType,
    });
  }

  const fiscalHint = getBalanceTypeFiscalHint(
    selectedEntry,
    taxRegimes,
    form2072,
  );

  function changeFamily(option: BalanceTypeOption | null) {
    const nextFamily = option?.value ?? "";
    setPickedFamily(nextFamily);
    const candidates = nextFamily ? typeOptionsForFamily(nextFamily) : [];
    setFieldValue(
      typeName,
      candidates.length === 1 ? candidates[0].value : "",
    )?.catch((error: unknown) => console.error(error));
  }

  return (
    <div className={styles.cascade}>
      <ReactSelectStandalone
        label="Catégorie d'opération"
        placeholder="Choisir une catégorie"
        name={`${prefix}family`}
        inputId={`${prefix}family`}
        options={familyOptions}
        value={familyOption}
        onChange={changeFamily}
        isClearable
        isDisabled={disabled}
        mandatory
      />
      <div className={styles.type}>
        <ReactSelect
          label="Type d'opération"
          placeholder={
            family ? "Choisir un type" : "Rechercher ou choisir un type"
          }
          labelAction={typeFilter}
          name={typeName}
          options={typeOptions}
          mandatory
          isDisabled={disabled}
        />
        {fiscalHint && <Text variant="deci">{fiscalHint}</Text>}
      </div>
    </div>
  );
}
